/**
 * Tooltip positioning for the 今期進捗状況 (Current Progress) chart.
 *
 * work_items_82: ECharts otherwise picks a side that can cover the chart or run
 * past an iframe edge, truncating the tooltip. This helper is right-preferred,
 * left-fallback, then clamped inside the chart view so the full tooltip stays
 * visible at PC and SP widths.
 *
 * Pure function so it can be unit tested without importing `echarts-for-react`.
 */

/** Gap (px) between the pointer/axis point and the tooltip box. */
export const CURRENT_PROGRESS_TOOLTIP_GAP = 10;

export interface TooltipPositionSize {
  /** [width, height] of the chart view (the ECharts container). */
  viewSize: [number, number];
  /** [width, height] of the rendered tooltip content. */
  contentSize: [number, number];
}

const clamp = (value: number, min: number, max: number): number => {
  // When content is larger than the available view, `max` < `min`; fall back to
  // the gap edge (`min`) rather than returning a negative coordinate.
  if (max < min) {
    return min;
  }
  return Math.min(Math.max(value, min), max);
};

export const computeCurrentProgressTooltipPosition = (
  point: [number, number],
  size: TooltipPositionSize,
  gap: number = CURRENT_PROGRESS_TOOLTIP_GAP,
): [number, number] => {
  const [pointX, pointY] = point;
  const [viewWidth, viewHeight] = size.viewSize;
  const [contentWidth, contentHeight] = size.contentSize;

  // Horizontal: prefer the right side, fall back to the left when it overflows.
  const rightX = pointX + gap;
  const fitsRight = rightX + contentWidth <= viewWidth - gap;
  const candidateX = fitsRight ? rightX : pointX - contentWidth - gap;
  const x = clamp(candidateX, gap, viewWidth - contentWidth - gap);

  // Vertical: center on the point, then clamp inside the view.
  const centeredY = pointY - contentHeight / 2;
  const y = clamp(centeredY, gap, viewHeight - contentHeight - gap);

  return [x, y];
};
